import type {
    OrderType,
    SatisfactionLevel,
    WorkOrder,
    WorkOrderFilter
} from '@idea2app/ai-work-order-service';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Column, Field, RestTable } from 'mobx-restful-table';
import { Component } from 'react';
import { Badge, Card } from 'react-bootstrap';

import { i18n, I18nKey, t } from '../model/Translation';
import workOrderStore from '../model/WorkOrder';

const OrderTypeLabels: Record<OrderType, string> = {
    normal: 'normal_order',
    duplicate: 'duplicate_order',
    sensitive: 'sensitive_order',
    responsible: 'responsible_order'
};

const OrderTypeColors: Record<OrderType, string> = {
    normal: 'success',
    duplicate: 'warning',
    sensitive: 'danger',
    responsible: 'info'
};

const SatisfactionLabels: Record<SatisfactionLevel, string> = {
    satisfied: 'satisfied',
    basic: 'basic',
    unsatisfied: 'unsatisfied'
};

const SatisfactionColors: Record<SatisfactionLevel, string> = {
    satisfied: 'success',
    basic: 'warning',
    unsatisfied: 'danger'
};

@observer
export class WorkOrderListPage extends Component {
    @computed
    get filterFields(): Field<WorkOrderFilter>[] {
        return [
            { key: 'keywords', renderLabel: t('search') },
            { key: 'orderNumber', renderLabel: t('order_number') },
            { key: 'department', renderLabel: t('department') },
            {
                key: 'orderType',
                type: 'select',
                renderLabel: t('order_type'),
                options: [
                    { value: 'normal', label: t('normal_order') },
                    { value: 'duplicate', label: t('duplicate_order') },
                    { value: 'sensitive', label: t('sensitive_order') },
                    { value: 'responsible', label: t('responsible_order') }
                ]
            },
            {
                key: 'satisfactionLevel',
                type: 'select',
                renderLabel: t('satisfaction_level'),
                options: [
                    { value: 'satisfied', label: t('satisfied') },
                    { value: 'basic', label: t('basic') },
                    { value: 'unsatisfied', label: t('unsatisfied') }
                ]
            }
        ];
    }

    @computed
    get columns(): Column<WorkOrder>[] {
        return [
            {
                key: 'id',
                type: 'number',
                renderHead: t('order_id')
            },
            {
                key: 'orderNumber',
                renderHead: t('order_number')
            },
            {
                key: 'orderType',
                renderHead: t('order_type'),
                renderBody: ({ orderType }) => (
                    <Badge bg={OrderTypeColors[orderType]}>
                        {t(OrderTypeLabels[orderType] as I18nKey)}
                    </Badge>
                )
            },
            {
                key: 'category',
                renderHead: t('category')
            },
            {
                key: 'department',
                renderHead: t('department')
            },
            {
                key: 'acceptanceTime',
                renderHead: t('acceptance_time'),
                renderBody: ({ acceptanceTime }) =>
                    new Date(acceptanceTime).toLocaleString()
            },
            {
                key: 'acceptanceContent',
                renderHead: t('acceptance_content'),
                renderBody: ({ acceptanceContent }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {acceptanceContent}
                    </div>
                )
            },
            {
                key: 'processingStatus',
                renderHead: t('processing_status'),
                renderBody: ({ processingStatus }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {processingStatus}
                    </div>
                )
            },
            {
                key: 'satisfactionLevel',
                renderHead: t('satisfaction_level'),
                renderBody: ({ satisfactionLevel }) =>
                    satisfactionLevel ? (
                        <Badge bg={SatisfactionColors[satisfactionLevel]}>
                            {t(
                                SatisfactionLabels[satisfactionLevel] as I18nKey
                            )}
                        </Badge>
                    ) : null
            },
            {
                key: 'isClassificationOptimized',
                renderHead: t('ai_optimization_reason'),
                renderBody: ({ isClassificationOptimized }) =>
                    isClassificationOptimized ? (
                        <Badge bg="primary">{t('edit')}</Badge>
                    ) : (
                        <Badge bg="secondary">{t('cancel')}</Badge>
                    )
            },
            {
                key: 'uploadTime',
                renderHead: t('upload_time'),
                renderBody: ({ uploadTime }) =>
                    new Date(uploadTime).toLocaleString()
            }
        ];
    }

    render() {
        return (
            <Card className="m-3" body>
                <RestTable
                    filterFields={this.filterFields}
                    columns={this.columns}
                    store={workOrderStore}
                    translator={i18n}
                    striped
                    hover
                />
            </Card>
        );
    }
}
